import React, { useEffect, useState } from "react";
import Joyride, { CallBackProps, ACTIONS, STATUS } from "react-joyride";
import { Typography, Button, RadioButton } from "components/atoms";
import { useAppDispatch, useScreenSize } from "hooks";
import {
  useGetOnboardingTourStatusQuery,
  useSaveOnboardingTourStatusMutation,
} from "services/onBoardingTour";
import {
  useGetCourseworkQuery,
  useGetOverallProgressQuery,
  useGetTestScoresAndPerformanceQuery,
} from "services/homeApi";
import { useSaveThemePreferenceMutation } from "services/theme";
import { Modal } from "../Modal";
import "./index.scss";
import { decodeToken } from "utils/functions/decodeToken";
import {
  DASHBOARD_PROGRAME,
  MCAT_TEST_TYPE,
  SELF_PACED_PROGRAME,
} from "utils/constants";

interface toolTipProps {
  continuous: boolean;
  index: number;
  step: any;
  backProps: any;
  closeProps: any;
  primaryProps: any;
  tooltipProps: any;
  size: number;
  skipProps: any;
  isLastStep: boolean;
}

export const SelfPacedJoyRide = () => {
  const [run, setRun] = useState(false);
  const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);
  const [isFirstRadioSelect, setIsFirstRadioSelect] = useState(true);
  const [isSecondRadioSelect, setIsSecondRadioSelect] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [steps, setSteps] = useState<any>([]);
  const dispatch: any = useAppDispatch();

  const payload = decodeToken();

  const userId = payload ? payload.userid : null;
  const productCode = payload ? payload.productcode : null;
  const offeringCode = payload ? payload.offeringcode : null;

  const isDashboard =
    payload &&
    payload.hasOwnProperty("programtype") &&
    payload.programtype === DASHBOARD_PROGRAME;

  const isSelfPaced =
    payload &&
    payload.hasOwnProperty("programtype") &&
    payload.programtype === SELF_PACED_PROGRAME;

  const testtype =
    payload && payload.hasOwnProperty("testtype") && payload.testtype;

  const { isLoading, data, isError } = useGetOnboardingTourStatusQuery();
  const {
    isLoading: testScoreAndPerformanceLoading,
    isError: testScoreAndPerformanceError,
  } = useGetTestScoresAndPerformanceQuery();
  const {
    data: courseworkData,
    isLoading: courseWorkLoading,
    isError: courseWorkError,
  } = useGetCourseworkQuery({
    page: 1,
    ...(testtype === MCAT_TEST_TYPE ? { subject: 0 } : {}),
  });

  const { isLoading: overallProgressLoading, isError: overallProgressError } =
    useGetOverallProgressQuery({
      Userid: userId,
      Productcode: productCode,
      Offeringcode: offeringCode,
    });

  // const isLoading = false;
  // const data = null;
  // const isError = false;

  const [PostTour, { isLoading: postTourIsLoading, isError: postTourIsError }] =
    useSaveOnboardingTourStatusMutation();

  const [
    themeBody,
    { isLoading: themeBodyIsLoading, isError: themeBodyIsError },
  ] = useSaveThemePreferenceMutation();

  const screen = useScreenSize();

  const handleThemeModalClose = async () => {
    let selectedTheme: "light" | "dark" = isFirstRadioSelect ? "light" : "dark";

    try {
      await themeBody({
        theme: selectedTheme,
      })
        .unwrap()
        .then(() => {
          setIsThemeModalOpen((prev) => !prev);
        });
    } catch (error: any) {
      console.log("error : ", error);
    }
  };

  const handleThemeModalDefaultClose = () => {
    setIsThemeModalOpen((prev) => !prev);
  };

  const handleRadioBtnClick = (type: string) => {
    if (type === "first") {
      if (isFirstRadioSelect) {
        setIsFirstRadioSelect((prev) => false);
        setIsSecondRadioSelect((prev) => true);
      } else {
        setIsFirstRadioSelect((prev) => true);
        setIsSecondRadioSelect((prev) => false);
      }
    } else {
      if (isSecondRadioSelect) {
        setIsFirstRadioSelect((prev) => true);
        setIsSecondRadioSelect((prev) => false);
      } else {
        setIsFirstRadioSelect((prev) => false);
        setIsSecondRadioSelect((prev) => true);
      }
    }
  };

  const Tooltip = ({
    continuous,
    index,
    step,
    backProps,
    closeProps,
    primaryProps,
    tooltipProps,
    size,
    skipProps,
    isLastStep,
  }: toolTipProps) => {
    return (
      <div className="guided-tour-wrapper">
        <div className="body-wrapper">
          <Typography
            variant="h8Med"
            label={step.content}
            styles="guid-tour-text-custom-style"
          />
        </div>

        <div className="footer-wrapper">
          {/* <div className="left-btn">
            <Typography variant="h9Medium" label={`${index + 1}/${size}`} />
          </div> */}
          <div className="right-btn">
            <div className="skip-wrapper" onClick={skipProps.onClick}>
              <Typography
                variant="h9Medium"
                label={skipProps.title}
                styles="skip-text-custom-style"
              />
            </div>

            <Button
              label="Back"
              type="filter"
              icon={false}
              styles="back-button-custom-style"
              isLoading={postTourIsLoading}
              onClick={backProps.onClick}
              isDisabled={index === 0}
            />

            <Button
              label={isLastStep ? "Finish" : "Next"}
              type="appeal"
              icon={false}
              onClick={primaryProps.onClick}
              styles="button-custom-style"
              isLoading={postTourIsLoading}
            />
          </div>
        </div>
      </div>
    );
  };

  const staticSteps = [
    {
      content:
        "Welcome! 👋  Start your journey by clicking “Click here to start! View your prep info” on the Home page. Access your Student Guide, learn how to get started, and explore everything you need to succeed.",
      target: "#dashboard-self-paced-view-prep-info-btn",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    {
      content:
        "On the Home page, you can view your course progress by clicking on the “Overall Progress Summary” card.",
      target: ".doughnut-card-section",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    {
      content:
        "Click on “Test Scores & Performance” to view your coursework performance.",
      target: ".horizontal-progress-bar-card-section",
      disableBeacon: true,
      disableOverlayClose: true,
    },
  ];

  const dynamicSteps = {
    "Lessons": {
      content: "Complete your lessons within this Lessons segment tab.",
      target: "#self-paced-lessons-tab",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    "Lesson": {
      content: "Complete your lessons within this Lesson segment tab.",
      target: "#self-paced-lesson-tab",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    "Required Homework": {
      content:
        "Complete homework after class to test your knowledge and enforce your learning.",
      target: "#self-paced-required-homework-tab",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    "Math Lessons": {
      content:
        "Complete your math lessons within this Math Lessons segment tab.",
      target: "#self-paced-math-lessons-tab",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    "Math Quick Reviews": {
      content:
        "Complete your math reviews within this Math Quick Reviews segment tab.",
      target: "#self-paced-math-quick-reviews-tab",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    "Data Insights (DI) Lessons": {
      content:
        "Complete your data insights lessons within this Data Insights (DI) Lessons segment tab.",
      target: "#self-paced-data-insights-(di)-lessons-tab",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    "DI Quick Reviews": {
      content:
        "Complete your data insights reviews within this DI Quick Reviews segment tab.",
      target: "#self-paced-di-quick-reviews-tab",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    "Verbal Lessons": {
      content:
        "Complete your verbal lessons within this Verbal Lessons tab.",
      target: "#self-paced-verbal-lessons-tab",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    "Math Practice": {
      content:
        "Complete your math practice within this Math Practice segment tab.",
      target: "#self-paced-math-practice-tab",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    "Diagnostic Exam": {
      content:
        "Complete your diagnostic exam within this class segment tab.",
      target: "#self-paced-diagnostic-exam-tab",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    "Diagnostic Exams": {
      content:
        "Complete your diagnostic exam within this class segment tab.",
      target: "#self-paced-diagnostic-exams-tab",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    "Diagnostic Exam A": {
      content:
        "Complete your diagnostic exam within this class segment tab.",
      target: "#self-paced-diagnostic-exam-a-tab",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    "Diagnostic Exam B": {
      content:
        "Complete your diagnostic exam within this class segment tab.",
      target: "#self-paced-diagnostic-exam-b-tab",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    "Prework": {
      content:
        "Complete your Prework within this class segment tab.",
      target: "#self-paced-prework-tab",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    "Homework": {
      content:
        "Complete your Prework within this class segment tab.",
      target: "#self-paced-homework-tab",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    "Video Lessons": {
      content:
        "Watch Video Lessons within this class segment tab.",
      target: "#self-paced-video-lessons-tab",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    "Drills": {
      content:
        "Complete your Drills within this class segment tab.",
      target: "#self-paced-drills-tab",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    "subjectsDropdown": {
      content:
        "Refine your coursework search results though these filter options.",
      target: "#self-paced-subjects-drop-down",
      disableBeacon: true,
      disableOverlayClose: true,
    },
  };

  type StepKey = keyof typeof dynamicSteps;

  const styles = {
    spotlight: {
      border: "5px solid #3272BE",
    },

    options: {
      overlayColor: "rgba(0, 0, 0, 0.25)",
    },
  };

  const handleRun = () => {
    if (screen.width > 574) {
      setRun((prev) => true);
    }
  };

  const joyRideCallBack = async ({
    index,
    size,
    status,
    type,
    action,
    step,
  }: CallBackProps) => {
    if (type === "error:target_not_found") {
      if (index + 1 === size) {
        // check if this last step
        setRun((prev) => false);
        setIsThemeModalOpen((prev) => true);
      } else {
        setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
      }
    }

    if (type === "step:before") {
      try {
        await PostTour({
          totalSteps: steps.length,
          currentStep: index,
          skipped: false,
        }).unwrap();
      } catch (error: any) {
        console.log("error : ", error);
      }
    }

    if (type === "step:after") {
      // setStepIndex((prev) => prev + 1);
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
    }

    if (ACTIONS.SKIP === action) {
      try {
        await PostTour({
          totalSteps: steps.length,
          currentStep: index,
          skipped: true,
        })
          .unwrap()
          .then(() => {
            setRun((prev) => false);
            setIsThemeModalOpen((prev) => true);
          });
      } catch (error: any) {
        console.log("error : ", error);
      }
    }

    let activeCarousel = document.querySelector(".active-default-carousel");
    let item = document.getElementById("dot-1");
    let item2 = document.getElementById("dot-0");

    // when the user clicks on the next button after the step related to test-scores-and-performance carosal should move to test-scores-and-performance card

    if (
      index === 1 &&
      type === "step:after" &&
      ACTIONS.CLOSE === action &&
      activeCarousel &&
      !activeCarousel.querySelector("#test-scores-and-performance")
    ) {
      setRun(false);
      item?.click();
      const timer = setTimeout(() => {
        setRun(true);
        clearTimeout(timer);
      }, 200);

      return () => clearTimeout(timer);
    }

    // when the user clicks on the back button after the step related to test-scores-and-performance carosal should move to doughnut-card-section card
    if (
      index === 2 &&
      type === "step:after" &&
      ACTIONS.PREV === action &&
      activeCarousel &&
      !activeCarousel.querySelector("#doughnut-card-section")
    ) {
      setRun(false);
      item2?.click();
      const timer = setTimeout(() => {
        setRun(true);
        clearTimeout(timer);
      }, 200);

      return () => clearTimeout(timer);
    }

    // when the user refresh the browser and joiride in test-scores-and-performance card need to carosal move to it
    if (
      index === 2 &&
      type === "tour:start" &&
      activeCarousel &&
      !activeCarousel.querySelector("#test-scores-and-performance")
    ) {
      setRun(false);
      item?.click();
      const timer = setTimeout(() => {
        setRun(true);
        clearTimeout(timer);
      }, 200);

      return () => clearTimeout(timer);
    }
    // when the user refresh the browser and joiride in section which after test score and performance card and click back button. carosal need to move to it
    if (
      index === 2 &&
      type === "step:before" &&
      ACTIONS.PREV === action &&
      activeCarousel &&
      !activeCarousel.querySelector("#test-scores-and-performance")
    ) {
      setRun(false);
      item?.click();
      const timer = setTimeout(() => {
        setRun(true);
        clearTimeout(timer);
      }, 200);

      return () => clearTimeout(timer);
    }

    if (status === STATUS.FINISHED) {
      try {
        await PostTour({
          totalSteps: steps.length,
          currentStep: steps.length,
          skipped: true,
        })
          .unwrap()
          .then(() => {
            setRun((prev) => false);
            setIsThemeModalOpen((prev) => true);
          });
      } catch (error: any) {
        console.log("error : ", error);
      }
    }
  };

  useEffect(() => {
    if (screen.width <= 574) {
      setRun((prev) => false);
    }
  }, [screen.width]);

  useEffect(() => {
    if (screen.width > 574) {
      if (
        (data === null || (data && !data.Skipped)) &&
        !isError &&
        !testScoreAndPerformanceLoading &&
        !courseWorkLoading &&
        !overallProgressLoading &&
        !testScoreAndPerformanceError &&
        !courseWorkError &&
        !overallProgressError
      ) {
        if (data && data.CurrentStep > 0) {
          setStepIndex((prev) => data.CurrentStep);
        }
        handleRun();
      }
    }
  }, [
    data,
    testScoreAndPerformanceLoading,
    courseWorkLoading,
    overallProgressLoading,
  ]);

  useEffect(() => {
    let copySteps: any = [...staticSteps];
    // this if for remove step related to performance-summary when showperformancesummary is false
    if (
      payload &&
      payload.hasOwnProperty("showperformancesummary") &&
      payload.showperformancesummary.toLowerCase() === "false"
    ) {
      copySteps.splice(1, 1);
    }

    if(testtype === MCAT_TEST_TYPE){
      copySteps.push(dynamicSteps["subjectsDropdown" as StepKey]);
    }

    if (
      courseworkData &&
      courseworkData.LearningTracks &&
      Array.isArray(courseworkData.LearningTracks) &&
      courseworkData.LearningTracks.length > 0
    ) {

      const filteredTracks = courseworkData.LearningTracks.filter(
        (track: any) => Array.isArray(track.Groups) && track.Groups.length > 0
      );

      const uniqueGroupNames = filteredTracks[0]?.Groups.map(
        (group: any) => group.Name
      ) || [];

      if (Array.isArray(uniqueGroupNames) && uniqueGroupNames.length > 0) {
        uniqueGroupNames.forEach((groupName: string) => {
          if (groupName in dynamicSteps) {
            copySteps.push(dynamicSteps[groupName as StepKey]);
          }
        });
      }
    }

    setSteps([...copySteps]);
  }, [courseworkData]);

  return (
    <>
      {!isLoading && !isError && !courseWorkLoading && (
        <Joyride
          tooltipComponent={Tooltip}
          run={run}
          steps={steps}
          styles={styles}
          callback={joyRideCallBack}
          stepIndex={stepIndex}
        />
      )}
      {isThemeModalOpen && (
        <Modal
          closeOnOutsideClick={false}
          isOpen={isThemeModalOpen}
          onClose={handleThemeModalDefaultClose}
          theme="light"
        >
          <div className="theme-modal-wrapper">
            <div className="theme-modal-header">
              <Typography
                variant="h8Med"
                label="Choose your theme"
                styles="header-custom-style"
              />
            </div>
            <div className="theme-modal-body">
              <div className="content-section">
                <div className="radio-btn-section">
                  <div className="radio">
                    <RadioButton
                      isSelected={isFirstRadioSelect}
                      onClick={handleRadioBtnClick}
                      type="first"
                    />
                    <div className="mode">
                      <Typography variant="h8Med" label="Light Mode" />
                    </div>
                  </div>
                  <div className="radio">
                    <RadioButton
                      isSelected={isSecondRadioSelect}
                      onClick={handleRadioBtnClick}
                      type="second"
                    />
                    <div className="mode">
                      <Typography variant="h8Med" label="Dark Mode" />
                    </div>
                  </div>
                </div>
                <div className="typography-section">
                  <Typography
                    variant="h8Book"
                    label="You can change your theme anytime within My Account > Settings"
                  />
                </div>
              </div>
              <div className="btn-section">
                <Button
                  label="Save"
                  icon={false}
                  type="score"
                  onClick={handleThemeModalClose}
                  isLoading={themeBodyIsLoading}
                />
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};
